import { collect } from "collect.js";
import { useFormatsMoney } from "./FormatsMoney";
import GlobalEventBus from "../EventBus/GlobalEventBus";
import { useCustomerStore } from "../stores/CustomerStore";
import { defineStore } from "pinia";
import { Price, ProductVariable } from "@daalder/types";

const { formatMoney } = useFormatsMoney();
let customerStore: Omit<ReturnType<typeof useCustomerStore>, keyof ReturnType<typeof defineStore>> = null;

/**
 * @description This composable contains methods specifically for determining prices out of a collection of Prices. Any
 * methods more abstract than this should be moved to separate composables, like the DiscountHelper. Formatting should
 * also be moved out of this composable.
 */
export function usePriceHelper() {
    function getValidatedPrices(prices: any) {
        // Check if prices are not null/undefined
        if (prices === null || prices === undefined) {
            return null;
        }

        return collect().wrap(Array.isArray(prices) ? prices : [prices]);
    }

    function getLowestPriceAsObject(prices: any, amount: number = 1) {
        // Make sure amount is at least 1
        if (!amount || amount < 1) {
            amount = 1;
        }

        // Validate and format prices
        prices = getValidatedPrices(prices);

        // Check if prices are not null
        if (prices === null) {
            return null;
        }

        return (
            prices
                // Filter for amount.
                .filter((price: { amount: number }) => {
                    return price.amount <= amount;
                })
                // Sort prices lowest to highest
                .sortBy((price: Price) => {
                    // price.price is a string, parse to float
                    return parseFloat(price.price);
                })
                // Return lowest
                .first()
        );
    }

    function getPrice(prices: any, amount: number = 1) {
        let price = getLowestPriceAsObject(prices, amount);
        if (price === null || price === undefined) {
            return null;
        }

        return exclVat() ? price.price_excluding_vat : price.price;
    }

    function getLowestListPriceAsObject(prices: any, amount: number = 1) {
        // Make sure amount is at least 1
        if (!amount || amount < 1) {
            amount = 1;
        }

        // Validate and format prices
        prices = getValidatedPrices(prices);

        // Check if prices are not null
        if (prices === null) {
            return null;
        }

        return (
            prices
                // Filter for amount
                .filter((price: { amount: number }) => {
                    return price.amount <= amount;
                })
                // Where list_price is set
                .whereNotNull("list_price")
                // Sort prices lowest to highest
                .sortBy((price: Price) => {
                    // price.list_price is a string, parse to float
                    return parseFloat(price.list_price);
                })
                // Return lowest
                .first()
        );
    }

    function getListPrice(prices: any, amount: number = 1) {
        let listPrice = getLowestListPriceAsObject(prices, amount);
        if (listPrice === null || listPrice === undefined) {
            return null;
        }

        return exclVat() ? listPrice.list_price_excluding_vat : listPrice.list_price;
    }

    function getFormattedPrice(prices: any, amount: number = 1, withCurrency = true) {
        let price = getLowestPriceAsObject(prices, amount);
        if (price === null || price === undefined) {
            return null;
        }

        let currency = price.currency;
        price = exclVat() ? price.price_excluding_vat : price.price;
        return formatMoney(price, currency, true, withCurrency);
    }

    function getFormattedListPrice(prices: any, amount: number = 1, withCurrency = true) {
        let listPrice = getLowestListPriceAsObject(prices, amount);
        if (listPrice === null || listPrice === undefined) {
            return null;
        }

        let currency = listPrice.currency;
        listPrice = exclVat() ? listPrice.list_price_excluding_vat : listPrice.list_price;
        return formatMoney(listPrice, currency, true, withCurrency);
    }

    function getNextPriceTierAsObject(prices: any, currentAmount: number = 1) {
        if (prices === null || prices === undefined) {
            return null;
        }

        if (!(prices instanceof collect)) {
            prices = collect(prices);
        }

        return (
            prices
                // Filter for amount
                .filter((price: { amount: number }) => {
                    return price.amount > currentAmount;
                })
                // Sort prices lowest to highest
                .sortBy((price: Price) => {
                    // price.price is a string, parse to float
                    return parseFloat(price.price);
                })
                // Sort prices by amount (lowest to highest)
                .sortBy((price: Price) => {
                    // Make sure that price.amount is an integer
                    return parseInt(String(price.amount));
                })
                // Return lowest
                .first()
        );
    }

    function exclVat(): boolean {
        if (customerStore === null) {
            customerStore = useCustomerStore();
        }
        return customerStore.customer?.company?.show_prices_excluding_vat == 1;
    }

    function inclVat(): boolean {
        return exclVat() === false;
    }

    function getOriginalPrice(prices: any, amount = 1) {
        amount = amount === 0 ? 1 : amount;

        let price = getLowestPriceAsObject(prices, amount);
        price = exclVat() ? price?.original_price_excluding_vat : price?.original_price;

        if (price === null || price === undefined) {
            return null;
        }

        return price;
    }

    /**
     * @deprecated This should probably be removed/absorbed into other methods. It's oddly specific.
     */
    function getWizardCartProductPrice(prices: any, amount = 1, excludingDiscounts = false) {
        if (excludingDiscounts) {
            let listPrice = getListPrice(prices, amount);
            let originalPrice = getOriginalPrice(prices, amount);

            if (listPrice !== null && listPrice !== undefined && listPrice != 0) {
                return listPrice;
            }

            if (originalPrice !== null && originalPrice !== undefined && originalPrice != 0) {
                return originalPrice;
            }
        }

        let price = getPrice(prices, amount);
        if (price === null || price === undefined) {
            return 0;
        }

        return price;
    }

    function getProductOptionsPrice(variables: ProductVariable[], optionValues: { [key: string]: any }) {
        if (!variables || variables.length === 0) {
            return 0;
        }

        let totalPrice = 0;

        Object.keys(optionValues).forEach((optionId) => {
            let variable = variables.find((e) => {
                return e.id === parseInt(optionId);
            });

            let optionValue = optionValues[parseInt(optionId)];
            if (Array.isArray(optionValue)) {
                optionValue.forEach((value) => {
                    let option = variable.options.find((e) => {
                        return e.id === value;
                    });

                    if (option) {
                        let price = parseFloat(exclVat() ? option.price?.price_excluding_vat : option.price?.price);
                        totalPrice += price;
                    }
                });
            } else if (optionValue !== "") {
                let option = variable.options.find((e) => {
                    return e.id === optionValue;
                });

                if (option) {
                    let price = parseFloat(exclVat() ? option.price?.price_excluding_vat : option.price?.price);
                    totalPrice += price;
                }
            }
        });

        return totalPrice;
    }

    return {
        getLowestPriceAsObject,
        getPrice,
        getLowestListPriceAsObject,
        getListPrice,
        getFormattedPrice,
        getFormattedListPrice,
        getNextPriceTierAsObject,
        exclVat,
        inclVat,
        getOriginalPrice,
        getWizardCartProductPrice,
        getProductOptionsPrice,
    };
}
